import type { ConnectionQuality } from '@live-stream/shared'
import type { ConnectionState, PeerStats } from '@live-stream/media'

/**
 * Connection quality — the pure part.
 *
 * The media store polls the transport every two seconds and feeds the
 * numbers here; this file turns them into excellent / good / poor / lost
 * without flapping. Everything runs under vitest with no WebRTC.
 */

export const QUALITY_RANK: Record<ConnectionQuality, number> = {
  lost: 0,
  poor: 1,
  good: 2,
  excellent: 3,
}

/** One interval's worth of evidence about one link. */
export interface LinkWindow {
  connectionState: ConnectionState
  roundTripTimeMs: number | null
  jitterMs: number
  /** Packets in THIS interval, not since the call began. */
  packetsReceived: number
  packetsLost: number
  /** Video freezes in this interval. */
  freezes: number
}

/**
 * Difference two cumulative samples into one window.
 *
 * getStats() counters are lifetime totals, so a loss burst in minute one
 * would colour the whole call if rated directly. Only the change since the
 * previous sample says anything about *now*. With no previous sample the
 * totals so far stand in; the second poll corrects it.
 */
export function windowOf(prev: PeerStats | null, curr: PeerStats): LinkWindow {
  const delta = (now: number, before: number | undefined): number =>
    Math.max(0, now - (before ?? 0))
  return {
    connectionState: curr.connectionState,
    roundTripTimeMs: curr.roundTripTimeMs,
    jitterMs: curr.jitterMs,
    packetsReceived: delta(curr.packetsReceived, prev?.packetsReceived),
    packetsLost: delta(curr.packetsLost, prev?.packetsLost),
    freezes: delta(curr.freezeCount, prev?.freezeCount),
  }
}

/**
 * Rate one link for one interval.
 *
 * Loss is the strongest signal — 5% is where Opus concealment stops hiding
 * it and speech breaks up. Round trip above 400 ms makes people talk over
 * each other; jitter above 60 ms means the buffer is already stretching.
 * A freeze is a freeze.
 *
 * Returns null when there is no evidence either way: not yet connected, or
 * connected with nothing received this interval (their camera is off and
 * their mic is in silence). The caller keeps the previous rating rather than
 * inventing one.
 */
export function rateLink(w: LinkWindow): ConnectionQuality | null {
  if (w.connectionState === 'failed' || w.connectionState === 'closed') return 'lost'
  // 'disconnected' is often a transient blip that recovers on its own. Poor,
  // not lost — lost is for when ICE has actually given up.
  if (w.connectionState === 'reconnecting') return 'poor'
  if (w.connectionState !== 'connected') return null

  const total = w.packetsReceived + w.packetsLost
  if (total === 0) return null
  const loss = w.packetsLost / total
  const rtt = w.roundTripTimeMs ?? 0

  if (loss > 0.05 || rtt > 400 || w.jitterMs > 60 || w.freezes >= 3) return 'poor'
  if (loss > 0.01 || rtt > 200 || w.jitterMs > 30 || w.freezes >= 1) return 'good'
  return 'excellent'
}

/**
 * In a mesh, my quality is my BEST link.
 *
 * If my network is bad, every link is bad and the best is still bad. If one
 * peer's network is bad, only my link to them suffers — they will flag it
 * themselves, because all of *their* links are bad — and my best link says
 * I am fine. In a two-party call there is one link and both sides report
 * it, which is the honest answer. Null when alone: nothing to rate.
 */
export function ownQuality(links: ReadonlyArray<ConnectionQuality>): ConnectionQuality | null {
  let best: ConnectionQuality | null = null
  for (const q of links) if (best === null || QUALITY_RANK[q] > QUALITY_RANK[best]) best = q
  return best
}

/**
 * Hysteresis for one rated thing. Improving needs `improveAfter`
 * consecutive better samples, so a single clean interval in a rough patch
 * does not bounce the bars. Worsening shows at once by default — a call
 * going bad is news — but a link that stands for the whole call (the SFU's)
 * is asked for `worsenAfter` consecutive worse samples too, because one
 * two-second sample of jitter on a cellular link is not a call going bad,
 * and every change is a patch to everyone. 'lost' is never delayed: a
 * connection that has failed has failed.
 */
export class QualityTracker {
  private current: ConnectionQuality
  private candidate: ConnectionQuality | null = null
  private streak = 0

  constructor(
    initial: ConnectionQuality = 'good',
    private readonly improveAfter = 2,
    private readonly worsenAfter = 1,
  ) {
    this.current = initial
  }

  get value(): ConnectionQuality {
    return this.current
  }

  /** Feed one rating; null (no evidence) leaves the state alone. Returns the tracked rating. */
  update(rating: ConnectionQuality | null): ConnectionQuality {
    if (rating === null) return this.current
    if (rating === this.current) {
      this.candidate = null
      this.streak = 0
      return this.current
    }

    const worse = QUALITY_RANK[rating] < QUALITY_RANK[this.current]
    if (worse && (rating === 'lost' || this.worsenAfter <= 1)) return this.settle(rating)

    if (this.candidate === rating) this.streak++
    else {
      this.candidate = rating
      this.streak = 1
    }
    if (this.streak >= (worse ? this.worsenAfter : this.improveAfter)) return this.settle(rating)
    return this.current
  }

  private settle(rating: ConnectionQuality): ConnectionQuality {
    this.current = rating
    this.candidate = null
    this.streak = 0
    return this.current
  }
}
